// Image blob/file conversion utilities: JPEG conversion with size control, URL/dataURL to File.

import { createLogger } from '../../../utils/logger.js'

const log = createLogger('add-to-design')

/**
 * Convert any image blob to a JPEG File, optionally downscaling so the long
 * edge does not exceed maxLongEdge (keeps the SDS SDK upload under its size
 * ceiling). White background is flattened in (JPEG has no alpha). aura-sr emits
 * PNG; SDS requires JPEG — this is the conversion + size-control step.
 *
 * @param {Blob} blob - source image bytes (PNG / JPEG / etc.)
 * @param {string} fileName
 * @param {{ maxLongEdge?: number, targetLongEdge?: number, quality?: number, coverWidth?: number, coverHeight?: number }} [opts]
 * @returns {Promise<File>}
 */
// The '#FFFFFF' fills below are image-content colors, not themed UI chrome —
// the white JPEG underlay is baked into the exported/uploaded pixels and must
// not follow the theme.
export async function toJpeg(blob, fileName, opts = {}) {
  const quality = opts.quality ?? 0.92
  const maxLongEdge = opts.maxLongEdge ?? Infinity
  const targetLongEdge = opts.targetLongEdge ?? maxLongEdge

  const coverW = opts.coverWidth
  const coverH = opts.coverHeight
  const dimsFor = (w, h) => {
    // Cover mode (picture_request): downscale to the SMALLEST size where BOTH
    // dims still meet the required cover size. Long-edge downscaling is wrong
    // for portrait art vs a landscape picture_request — it can drop the short
    // edge below the requirement (5760x10240 -> long-edge 5500 = 3094x5500,
    // width 3094 < required 5000). max() of the two ratios keeps both >= cover.
    if (coverW && coverH && w > 0 && h > 0) {
      // Always scale so BOTH dims >= the required cover (picture_request).
      // Downscale when the image overshoots; UPSCALE when it is still short
      // (aura-sr 4x of a 1200px source = 4800 < 5000 required — the prior
      // `if (scale>=1) return {w,h}` left it at 4800, so SDS stored an
      // under-size 4800x4800 material and the order failed). The final
      // bicubic upscale here closes that small gap so the SDS material always
      // meets picture_request.
      const scale = Math.max(coverW / w, coverH / h)
      return { w: Math.max(coverW, Math.round(w * scale)), h: Math.max(coverH, Math.round(h * scale)) }
    }
    const longEdge = Math.max(w, h)
    if (longEdge <= maxLongEdge) return { w, h }
    const ratio = targetLongEdge / longEdge
    return { w: Math.round(w * ratio), h: Math.round(h * ratio) }
  }

  if (typeof OffscreenCanvas !== 'undefined' && typeof createImageBitmap !== 'undefined') {
    try {
      const bitmap = await createImageBitmap(blob)
      const { w, h } = dimsFor(bitmap.width, bitmap.height)
      const offscreen = new OffscreenCanvas(w, h)
      const ctx = offscreen.getContext('2d')
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, w, h)
      ctx.drawImage(bitmap, 0, 0, w, h)
      bitmap.close()
      const jpegBlob = await offscreen.convertToBlob({ type: 'image/jpeg', quality })
      return new File([jpegBlob], fileName, { type: 'image/jpeg' })
    } catch (e) {
      log.warn('toJpeg OffscreenCanvas fallback:', e.message)
    }
  }

  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      try {
        const { w, h } = dimsFor(img.naturalWidth, img.naturalHeight)
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, w, h)
        ctx.drawImage(img, 0, 0, w, h)
        canvas.toBlob((jpegBlob) => {
          URL.revokeObjectURL(img.src)
          if (jpegBlob) resolve(new File([jpegBlob], fileName, { type: 'image/jpeg' }))
          else reject(new Error('Failed to convert image to JPEG'))
        }, 'image/jpeg', quality)
      } catch (e) {
        URL.revokeObjectURL(img.src)
        reject(new Error('Canvas operation failed: ' + e.message))
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      reject(new Error('Failed to load image for JPEG conversion'))
    }
    img.src = URL.createObjectURL(blob)
  })
}

/**
 * Convert URL to File. SDS requires JPEG, so anything that is NOT already JPEG
 * (WebP, and notably the PNG that aura-sr emits) is converted. The final
 * injected material is therefore always a JPEG.
 */
export async function urlToFile(imageUrl, fileName) {
  log.info('Fetching image:', imageUrl)
  const response = await fetch(imageUrl, {
    mode: 'cors',
    headers: { Accept: 'image/jpeg, image/png, image/webp, image/*' },
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`)

  const blob = await response.blob()

  // SDS only accepts JPEG. Convert WebP AND PNG (aura-sr output) to JPEG.
  if (blob.type === 'image/webp' || blob.type === 'image/png') {
    log.info(`Converting ${blob.type} to JPEG for SDS compatibility...`)
    return await toJpeg(blob, fileName)
  }

  let mimeType = blob.type
  if (!mimeType || !mimeType.startsWith('image/')) {
    mimeType = 'image/jpeg'
  }

  return new File([blob], fileName, { type: mimeType })
}

/**
 * Convert data URL to File
 */
export async function dataUrlToFile(dataUrl, fileName) {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) u8arr[n] = bstr.charCodeAt(n)
  return new File([new Blob([u8arr], { type: mime })], fileName, { type: mime })
}
